import { Select, Space, Spin } from 'antd';
import NetworkIcon from 'components/NetworkIcon/NetworkIcon';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ninjaApi from 'store/redux/apis';
import { StatusFormatter } from 'components/tables/NinjaTable/formatters';
import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { LoadingOutlined } from '@ant-design/icons';
import NetworkSwitch from 'components/switches/NetworkSwitch';
import debounce from 'common/debounce';

const AdsetNameSelect = (props) => {
  const {
    value = [],
    network = 'all',
    networkSwitch = true,
    onChange,
    multiple = true,
    disableNetworksSelector,
    placeholder = 'Adset Name',
    disableSelectAll = true,
    id,
    label = 'Type To Search',
    preload = 20,
    ...rest
  } = props;

  const [search, setSearch] = useState();
  const [open, setOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [load, setLoad] = useState(preload);
  const [selectedNetwork, setSelectedNetwork] = useState(network);
  const [getSearchAdsetQuery, { data = [], isFetching }] = ninjaApi.useLazySearchAdsetQuery();
  const [loadingInitialOptions, setLoadingInitialOptions] = useState(false);

  const fetchAdset = useCallback(() => {
    getSearchAdsetQuery({
      network: selectedNetwork,
      search: search,
      id: id,
      preload: load,
    }).unwrap();
  }, [selectedNetwork, search, id, load]);

  const normalizedValue = useMemo(() => {
    return (value || []).map((v) => Number(v));
  }, [value]);

  useEffect(() => {
    const fetchInitialSelectedIds = async () => {
      if (!normalizedValue.length) return;
      try {
        setLoadingInitialOptions(true);
        await getSearchAdsetQuery({
          ids: normalizedValue,
        }).unwrap();
      } catch (err) {
        console.error('Initial fetch failed', err);
      } finally {
        setLoadingInitialOptions(false);
      }
    };

    fetchInitialSelectedIds();
  }, []);

  useEffect(() => {
    open && fetchAdset();
  }, [fetchAdset, open]);

  const handleChange = useCallback((value) => {
    onChange?.(value);
  });

  const handleNetworksChange = useCallback((network) => {
    setSelectedNetwork(network);
  });

  const options = useMemo(() => {
    return data.filter((acc) => selectedNetwork === 'all' || acc.network === selectedNetwork);
  }, [data, selectedNetwork]);

  useEffect(() => {
    if (scrollPosition > 90 && !(load > data.length)) {
      setTimeout(() => {
        setLoad(load + 50);
      }, 200);
    }
  }, [scrollPosition]);

  useEffect(() => {
    handleSearch('');
  }, [selectedNetwork]);

  const handleSearch = debounce((searchValue) => {
    setSearch(searchValue);
  }, 200);

  const renderNotFoundContent = () => {
    return (
      <LoadingOutlined className='flex items-center justify-center m-auto text-3xl h-9' spin />
    );
  };

  const handleClose = () => {
    setOpen(!open);
    setSearch('');
  };
  const onSearch = (value) => {
    handleSearch(value);
  };

  return (
    <Select
      className='w-full'
      open={open}
      mode='multiple'
      placeholder={open ? label : placeholder}
      maxTagCount='responsive'
      onChange={handleChange}
      value={loadingInitialOptions ? [] : normalizedValue}
      suffixIcon={loadingInitialOptions ? <LoadingOutlined /> : undefined}
      allowClear
      onDropdownVisibleChange={handleClose}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
        if (scrollPercentage > 90) {
          setScrollPosition(scrollPercentage);
        }
      }}
      notFoundContent={isFetching && options.length === 0 ? renderNotFoundContent() : undefined}
      onSearch={onSearch}
      filterOption={(input, option) => {
        const isMatch = option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        return isMatch;
      }}
      dropdownRender={(menu) => (
        <>
          {networkSwitch && (
            <NetworkSwitch
              selectedNetwork={selectedNetwork}
              handleNetworksChange={handleNetworksChange}
              disableNetworksSelector={disableNetworksSelector}
              disableSelectAll={disableSelectAll}
            />
          )}
          <div className='transition-all duration-300 '>
            {menu}
            {isFetching && options.length ? (
              <div className='w-full m-auto text-center'>
                <LoadingOutlined className='m-auto transition-all duration-300' />
              </div>
            ) : null}
          </div>
        </>
      )}
      {...rest}
    >
      {options?.map((opt, index) => {
        return (
          <Select.Option key={index} value={opt.id} label={opt.name}>
            <Space className='flex items-center justify-between w-full'>
              <div className='flex items-center justify-center w-full gap-2'>
                {opt.status && <StatusFormatter row={opt} title={false} />}
                <span role='img' aria-label={opt.name}>
                  <NetworkIcon network={opt.network} size={15} />
                </span>
                {opt.name}
              </div>
              {opt.type === 'search' && (
                <Icon className='text-color-light' path={mdiMagnify} size='1rem' />
              )}
            </Space>
          </Select.Option>
        );
      })}
    </Select>
  );
};
export default AdsetNameSelect;
